import * as React from "react";

import styled from "@emotion/styled";
import Tooltip from "@mui/material/Tooltip";

// Customizable mui components
import TableContainer from "../mui/TableContainer";
import Pagination from "../mui/Pagination";
import TableBody from "../mui/TableBody";
import TableCell from "../mui/TableCell";
import TableHead from "../mui/TableHead";
import TableRow from "../mui/TableRow";
import Table from "../mui/Table";
import Paper from "../mui/Paper";
import Stack from "../mui/Stack";
import Box from "../mui/Box";

// Common components
import MagicLoadingBox from "../common/MagicLoadingBox";

type RowData = {
  centerId: number;
  region: string;
  funder: string;
  centre: string;
  awardName: string;
  rank: number;
  programmePerformanceScore: number;
  finalScore: number;
  parameters: {
    parameterName: string;
    scoreAchieved: number;
    score: number;
  };
};

interface Props {
  handlePaginationChange: (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => void;

  activePaginationPageNo: number;
  rowData: any;
  isLoading: boolean;
  activeCentresPerformanceTable: string;
}

const CentresPerformanceTable: React.FC<Props> = (props) => {
  const {
    handlePaginationChange,
    activePaginationPageNo,
    rowData,
    isLoading = true,
    activeCentresPerformanceTable,
  } = props;

  let data: any[] = [];

  // handle loading stats of data
  if (isLoading) return <MagicLoadingBox height={500} />;

  if (rowData && rowData.length > 0 && Array.isArray(rowData)) {
    data = [...rowData];
  }

  let filterParametersByType = data.map((center:any) => ({
    ...center,
    parameters: center.parameters.filter((param:any) => param.parameterTypeName == activeCentresPerformanceTable)
  }));

  return (
    <Stack justifyContent="center">
      <StyledTableContainer component={Paper}>
        <StyledTable>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#CDCDCD",
                position: "sticky",
                top: "0px",
                zIndex: 6,
              }}
            >
              <StyledStickyHeaderTableCellLeft0>
                Region
              </StyledStickyHeaderTableCellLeft0>
              <StyledStickyHeaderTableCellLeft150>
                Funder
              </StyledStickyHeaderTableCellLeft150>
              <StyledStickyHeaderTableCellLeft300>
                Centre
              </StyledStickyHeaderTableCellLeft300>
              <ProgrammePerformanceScoreTableCell>
                {activeCentresPerformanceTable} Total Score
              </ProgrammePerformanceScoreTableCell>
              <StyledHeaderTableCell>Final Score</StyledHeaderTableCell>
              <StyledHeaderTableCell>Rank</StyledHeaderTableCell>
              <StyledHeaderTableCell>Medal Category</StyledHeaderTableCell>

              {/* @ts-ignore */}
              <ProgrammePerformanceParameterTableCell
                colSpan={filterParametersByType[0]?.parameters[0]?.parameterDetails?.length || []}
              >
                {activeCentresPerformanceTable} Parameters
              </ProgrammePerformanceParameterTableCell>
            </TableRow>

            <TableRow sx={{ backgroundColor: "#fffdf5" }}>
              <StyledStickyHeaderRowSecondTableCellLeft0 rowSpan={2} />
              <StyledStickyHeaderRowSecondTableCellLeft150 rowSpan={2} />
              <StyledStickyHeaderRowSecondTableCellLeft300 rowSpan={2} />

              <ProgrammePerformanceScoreTableCell1 rowSpan={2} align="center">
                100
              </ProgrammePerformanceScoreTableCell1>
              <StyledHeaderTableCell
                rowSpan={2}
                align="center"
                sx={{ verticalAlign: "bottom" }}
              >
                100
              </StyledHeaderTableCell>
              <StyledHeaderTableCell rowSpan={2} />
              <StyledHeaderTableCell
                rowSpan={2}
                colSpan={filterParametersByType.length < 1 ? 2 : 0}
              />

              {
                // @ts-ignore
                filterParametersByType[0]?.parameters[0]?.parameterDetails?.map((cols) => (
                  <StyledHeaderTableCell align="center">
                    <Tooltip title={cols.parameterName}>
                      {cols.parameterName.length > 20 ? (
                        <span>{cols.parameterName.slice(0, 20)}... </span>
                      ) : (
                        cols.parameterName
                      )}
                    </Tooltip>
                  </StyledHeaderTableCell>
                ))
              }
            </TableRow>

            <TableRow sx={{ backgroundColor: "#FFFDF4" }}>
              {
                // @ts-ignore
                filterParametersByType[0]?.parameters[0]?.parameterDetails?.map((cols) => (
                  <StyledHeaderTableCell2 align="center">
                    {cols.score}
                  </StyledHeaderTableCell2>
                ))
              }
            </TableRow>
          </TableHead>

          <TableBody>
            {filterParametersByType.length < 1 && (
              <TableCell colSpan={9} align="center">
                No data available
              </TableCell>
            )}

            {filterParametersByType.length > 0 &&
              filterParametersByType?.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <StyledStickyBodyTableCellLeft0>
                    {row.region}
                  </StyledStickyBodyTableCellLeft0>
                  <StyledStickyBodyTableCellLeft150>
                    {row.funderName}
                  </StyledStickyBodyTableCellLeft150>
                  <StyledStickyBodyTableCellLeft300>
                    {row.centerName}
                  </StyledStickyBodyTableCellLeft300>
                  <StyledBodyTableCell>
                    {row?.parameters[0]?.sumScoreAchieved}
                  </StyledBodyTableCell>
                  <StyledBodyTableCell>{row.totalSumScoreAchieved}</StyledBodyTableCell>
                  <StyledBodyTableCell>{row.rank}</StyledBodyTableCell>
                  <StyledBodyTableCell
                    sx={{
                      backgroundColor:
                        row.awardName == "Platinum"
                          ? "#A7A7A7"
                          : row.awardName == "Silver"
                          ? "#F6F6F6"
                          : row.awardName == "Gold"
                          ? "#FFF48D"
                          : row.awardName == "Bronze"
                          ? "#FFC85D"
                          : "",
                    }}
                  >
                    {row.awardName}
                  </StyledBodyTableCell>

                  {/* @ts-ignore */}
                  {row?.parameters[0]?.parameterDetails?.map((parameter, index) => (
                    <>
                      <StyledBodyTableCell
                        sx={{ backgroundColor: parameter.backgroundColor }}
                      >
                        {parameter.scoreAchieved}
                      </StyledBodyTableCell>
                    </>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>

      {/* Paginations  */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "8px",
          height: "68px",
          borderBottom: "1px solid #ECECEC",
          borderLeft: "1px solid #ECECEC",
          borderRight: "1px solid #ECECEC",
        }}
      >
        <Pagination
          count={filterParametersByType.length}
          onChange={handlePaginationChange}
          page={activePaginationPageNo}
        />
      </Box>
    </Stack>
  );
};

const StyledTableContainer = styled(TableContainer)({
  width: "100%",
  borderRadius: "8px",
  overflowX: "auto",
  // scrollbarWidth:"thin",
  border: "1px solid #ECECEC",
  minHeight: "500px",
  maxHeight: "500px",

  // For WebKit Browsers (Chrome, Edge, Safari)
  "&::-webkit-scrollbar": {
    width: "8px", // Width of the scrollbar
    height: "8px", // Height of the scrollbar for horizontal scrolling
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#D1D1D1", // Scrollbar thumb color
    borderRadius: "8px", // Rounded edges for the scrollbar thumb
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#B0B0B0", // Thumb color on hover
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#F5F5F5", // Track color
    borderRadius: "4px",
  },

  // For Firefox
  scrollbarWidth: "thin", // Thin scrollbar
  scrollbarColor: "#D1D1D1 #F5F5F5",
});

const StyledTable = styled(Table)({
  borderCollapse: "separate",
  width: "max-content",
  borderSpacing: "0px",
  tableLayout: "fixed",
});

// Sticky first three columns of header (Row No. 1)
const StyledStickyHeaderTableCellLeft0 = styled(TableCell)({
  position: "sticky",
  left: 0,
  zIndex: 6,
  backgroundColor: "#CDCDCD",
  fontWeight: 600,
  borderRight: "1px solid rgb(230, 228, 223)",
  width: "100px",
  padding: "8px",
  top: "0px",
});

const StyledStickyHeaderTableCellLeft150 = styled(
  StyledStickyHeaderTableCellLeft0
)({
  left: "100px",
  width: "250px",
  padding: "8px",
});

const StyledStickyHeaderTableCellLeft300 = styled(
  StyledStickyHeaderTableCellLeft0
)({
  left: "350px",
  width: "220px",
  padding: "8px",
});

// Sticky first three columns of header
const StyledStickyHeaderRowSecondTableCellLeft0 = styled(TableCell)({
  position: "sticky",
  left: 0,
  zIndex: 6,
  backgroundColor: "#fffdf5",
  fontWeight: 600,
  borderRight: "1px solid rgb(230, 228, 223)",
  width: "100px",
  padding: 8,
  top: "42px",
});

const StyledStickyHeaderRowSecondTableCellLeft150 = styled(
  StyledStickyHeaderRowSecondTableCellLeft0
)({
  left: "100px",
  width: "250px",
  padding: "8px",
  top: "42px",
});

const StyledStickyHeaderRowSecondTableCellLeft300 = styled(
  StyledStickyHeaderRowSecondTableCellLeft0
)({
  left: "350px",
  width: "220px",
  padding: "8px",
  top: "42px",
});

// Sticky first three columns of table body
const StyledStickyBodyTableCellLeft0 = styled(TableCell)({
  position: "sticky",
  left: "0px",
  backgroundColor: "#ffffff",
  borderRight: "1px solid rgb(230, 228, 223)",
  width: "100px",
  padding: "8px",
});

const StyledStickyBodyTableCellLeft150 = styled(StyledStickyBodyTableCellLeft0)(
  {
    left: "100px",
    width: "250px",
    padding: "8px",
  }
);

const StyledStickyBodyTableCellLeft300 = styled(StyledStickyBodyTableCellLeft0)(
  {
    left: "350px",
    width: "200px",
    padding: "8px",
  }
);

// Normal Header & Body Cell Styles
const StyledHeaderTableCell = styled(TableCell)({
  fontWeight: 600,
  fontSize: "14px",
  minWidth: "150px",
  border: "1px solid #ECECEC",
  padding: "8px",
  position: "sticky",
  backgroundColor: "#fffdf5",
  top: "42px",
  // zIndex: 6,
});
const StyledHeaderTableCell2 = styled(StyledHeaderTableCell)({
  top: "84px",
  // zIndex: 6,
});

const ProgrammePerformanceScoreTableCell = styled(TableCell)({
  fontWeight: 600,
  fontSize: "14px",
  minWidth: "250px",
  border: "1px solid #ECECEC",
  textAlign: "center",
  verticalAlign: "bottom",
  padding: "8px",
  position: "sticky",
  top: "0px",
});

const ProgrammePerformanceScoreTableCell1 = styled(
  ProgrammePerformanceScoreTableCell
)({
  top: "42px",
  backgroundColor: "#fffdf5",
});

const ProgrammePerformanceParameterTableCell = styled(TableCell)({
  fontWeight: 600,
  fontSize: "14px",
  border: "1px solid #ECECEC",
  textAlign: "center",
  padding: "8px",
  position: "sticky",
  top: "0",
});

const StyledBodyTableCell = styled(TableCell)({
  fontSize: "14px",
  minWidth: "150px",
  border: "1px solid #ECECEC",
  textAlign: "center",
  padding: "8px",
});

export default CentresPerformanceTable;
